import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Flex,
  Button,
  IconButton,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  HStack,
  Input,
  Select,
} from '@chakra-ui/react';
import { FiRefreshCw, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useTenant } from '../../contexts/TenantContext';
import { formatCallTime, useCallTimezone, zonedDayStartIso } from '../../utils/callTimezone';
import CallTimezoneSelect from '../../components/CallTimezoneSelect';
import { ExtensionsAPI, LogsAPI, ClientsAPI, CorrectionsAPI, getTenantUUID } from '../../services/api';
import PageHelp from '../../components/PageHelp';
import { useLanguage } from '../../i18n';
import { callEndReasonLabel } from '../../utils/callEndReason';
import { getPortalPageCopy } from '../../portalPageCopy';
import { CONTACT_FILTERS, callRefs, contactBadges, mergeTicketContacts } from './serviceRecords';
import { ChatRecordList } from './ChatRecords';
import { OutcomeBadges } from './CallOutcomeCard';
import { OUTCOME_FILTERS } from './callOutcomes';

const BADGE_COLORS = { phone: 'blue', email: 'purple', order: 'orange' };

const PAGE_SIZE = 50;

// variant="serviceRecords": the ecommerce 客服记录 view, with contact details per call.
// contactLookup(callRefs) returns ticket contact items for calls on the page.
const CallHistoryPage = ({ portalBasePath = '/app', variant = 'calls', contactLookup = null }) => {
  const isServiceRecords = variant === 'serviceRecords';
  // Service records cover phone calls and website Chatbox conversations.
  const [searchParams, setSearchParams] = useSearchParams();
  const source = isServiceRecords && searchParams.get('source') === 'chat' ? 'chat' : 'call';
  const navigate = useNavigate();
  const openCall = (call) => navigate(`${portalBasePath}/call-history/${call.call_id}`, { state: { call } });
  const { lang } = useLanguage();
  const baseCopy = getPortalPageCopy(lang, 'callHistory');
  const copy = isServiceRecords
    ? { ...baseCopy, pageTitle: lang === 'zh' ? '客服记录' : 'Service records', helpTitle: lang === 'zh' ? '客服记录' : 'Service records',
        helpDescription: lang === 'zh' ? '电话咨询逐条记录：客户留下的电话、邮箱、订单号直接显示在列表里，点开可查看对话和录音。' : 'Every customer conversation, with the phone, email and order number the customer left shown in the list.' }
    : baseCopy;
  const { currentTenant, getCurrentTenantInfo } = useTenant();
  const [displayTimezone, setDisplayTimezone] = useCallTimezone();
  const [calls, setCalls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestSequence = useRef(0);
  const [extensions, setExtensions] = useState([]);
  const [selectedExtension, setSelectedExtension] = useState('');
  // A9: search by caller number and date range (dates in the display zone).
  const emptySearch = { phone: '', from: '', to: '', contact: 'all', order: '' };
  const [search, setSearch] = useState(emptySearch);
  const [appliedSearch, setAppliedSearch] = useState(emptySearch);
  const [hasMore, setHasMore] = useState(false);
  // 未解决 / Unresolved: filters the loaded calls in the browser.
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const visibleCalls = calls.filter(OUTCOME_FILTERS[outcomeFilter] || OUTCOME_FILTERS.all);
  const toast = useToast();
  const tenantUUID = getCurrentTenantInfo()?.uuid || getTenantUUID();

  useEffect(() => {
    if (!tenantUUID) return;
    fetchData();
    const sequenceRef = requestSequence;
    return () => { sequenceRef.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant, tenantUUID]);

  const fetchData = async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setLoadError(false);
    setCalls([]);
    try {
      const userUUID = localStorage.getItem('userId');
      const contextUUID = tenantUUID || userUUID;
      const [extRes, contactsRes] = await Promise.all([
        ExtensionsAPI.list(),
        ClientsAPI.list().catch(() => []),
      ]);

      if (sequence !== requestSequence.current) return;
      const extData = Array.isArray(extRes) ? extRes : extRes?.data || [];
      setExtensions(extData);

      const contactsData = Array.isArray(contactsRes) ? contactsRes : contactsRes?.data || [];
      setContacts(contactsData);

      const extToUse = extData.some((ext) => ext.extension === selectedExtension)
        ? selectedExtension
        : extData[0]?.extension || '';
      setSelectedExtension(extToUse);
      if (!extToUse) {
        setCalls([]);
        return;
      }

      const callsRes = await LogsAPI.fetchCallLogs(extToUse, PAGE_SIZE, contextUUID, false, searchFilters(appliedSearch, 0));
      const page = await withTicketContacts(callsRes.logs || []);
      if (sequence === requestSequence.current) {
        setCalls(page);
        setHasMore((callsRes.logs || []).length === PAGE_SIZE);
      }
    } catch (error) {
      if (sequence !== requestSequence.current) return;
      setLoadError(true);
      toast({
        title: copy.loadError,
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  };

  const getContactName = (number) => {
    const contact = contacts.find(c => c.phone_number === number);
    return contact ? contact.name : number;
  };

  const searchFilters = (criteria, offset) => ({
    phone: criteria.phone.replace(/\D/g, '') || undefined,
    started_from: criteria.from ? zonedDayStartIso(criteria.from, displayTimezone) : undefined,
    started_before: criteria.to ? zonedDayStartIso(criteria.to, displayTimezone, true) : undefined,
    offset: offset || undefined,
    ...(isServiceRecords ? CONTACT_FILTERS[criteria.contact || 'all'] : {}),
    order: isServiceRecords ? (criteria.order || '').trim() || undefined : undefined,
  });

  // Ticket email/order numbers and correction counts come from other services;
  // never block the list on them.
  const withTicketContacts = async (page) => {
    if (!isServiceRecords || !page.length) return page;
    let enriched = page;
    if (contactLookup) {
      try {
        const result = await contactLookup(page.flatMap(callRefs));
        enriched = mergeTicketContacts(enriched, result?.items || []);
      } catch { /* list still shows call-side contacts */ }
    }
    try {
      const { counts } = await CorrectionsAPI.corrected(enriched.map((call) => call.call_id));
      enriched = enriched.map((call) => ({ ...call, corrections: counts?.[call.call_id] || 0 }));
    } catch { /* correction status is optional */ }
    return enriched;
  };

  const handleExtensionChange = (ext) => loadCalls(ext, appliedSearch, false);
  const runSearch = (event) => { event.preventDefault(); setAppliedSearch(search); loadCalls(selectedExtension, search, false); };
  const clearSearch = () => { setSearch(emptySearch); setAppliedSearch(emptySearch); loadCalls(selectedExtension, emptySearch, false); };

  const loadCalls = async (ext, criteria, append) => {
    const sequence = ++requestSequence.current;
    setSelectedExtension(ext);
    setLoading(true);
    setLoadError(false);
    if (!append) setCalls([]);
    try {
      const userUUID = localStorage.getItem('userId');
      const contextUUID = tenantUUID || userUUID;
      const offset = append ? calls.length : 0;
      const callsRes = await LogsAPI.fetchCallLogs(ext, PAGE_SIZE, contextUUID, false, searchFilters(criteria, offset));
      const merged = await withTicketContacts(callsRes.logs || []);
      if (sequence === requestSequence.current) {
        const page = merged;
        setCalls((current) => (append ? [...current, ...page] : page));
        setHasMore(page.length === PAGE_SIZE);
      }
    } catch (error) {
      if (sequence !== requestSequence.current) return;
      setLoadError(true);
      toast({
        title: copy.callsLoadFailed,
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    if (status === 'completed') return copy.completed;
    if (status === 'failed') return copy.failed;
    return status || copy.inProgress;
  };

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column" overflow="hidden">
      <Box flex="1" minH={0} overflowY="auto" p={6}>
      <PageHelp
        title={copy.helpTitle}
        description={copy.helpDescription}
        steps={[
          { title: copy.pickExtension, detail: copy.pickExtensionDetail },
          { title: copy.scanStatus, detail: copy.scanStatusDetail },
          { title: copy.openDetails, detail: copy.openDetailsDetail },
        ]}
        align="flex-end"
      />
      {isServiceRecords && (
        <HStack spacing={2} mb={4} role="tablist" aria-label={lang === 'zh' ? '来源' : 'Source'}>
          {[['call', lang === 'zh' ? '电话' : 'Phone calls'], ['chat', lang === 'zh' ? '网页客服 Chatbox' : 'Website Chatbox']].map(([key, label]) => (
            <Button key={key} size="sm" role="tab" aria-selected={source === key} variant={source === key ? 'solid' : 'outline'} colorScheme="blue"
              onClick={() => setSearchParams(key === 'chat' ? { source: 'chat' } : {})}>{label}</Button>
          ))}
        </HStack>
      )}
      {source === 'chat' ? <ChatRecordList portalBasePath={portalBasePath} lang={lang} /> : (<>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{copy.pageTitle}</Heading>
        <HStack spacing={3}>
          <CallTimezoneSelect value={displayTimezone} onChange={setDisplayTimezone} lang={lang} />
          <Select
            aria-label={copy.extension}
            value={selectedExtension}
            onChange={(e) => handleExtensionChange(e.target.value)}
            maxW="180px"
            size="sm"
          >
            {extensions.map((ext) => (
              <option key={ext.extension} value={ext.extension}>{ext.extension}</option>
            ))}
          </Select>
          <IconButton
            icon={<FiRefreshCw />}
            onClick={fetchData}
            isLoading={loading}
            aria-label={copy.refresh}
          />
        </HStack>
      </Flex>

      <form onSubmit={runSearch} aria-label={lang === 'zh' ? '搜索通话' : 'Search calls'}>
        <HStack spacing={3} mb={4} wrap="wrap" align="flex-end">
          <Input size="sm" maxW="200px" inputMode="tel" value={search.phone}
            aria-label={lang === 'zh' ? '来电号码' : 'Caller number'}
            placeholder={lang === 'zh' ? '来电号码（可输入部分）' : 'Caller number (partial)'}
            onChange={(e) => setSearch({ ...search, phone: e.target.value })} />
          <Input size="sm" maxW="170px" type="date" value={search.from}
            aria-label={lang === 'zh' ? '开始日期' : 'From date'}
            onChange={(e) => setSearch({ ...search, from: e.target.value })} />
          <Input size="sm" maxW="170px" type="date" value={search.to}
            aria-label={lang === 'zh' ? '结束日期' : 'To date'}
            onChange={(e) => setSearch({ ...search, to: e.target.value })} />
          {isServiceRecords && (
            <>
              <Select size="sm" maxW="170px" value={search.contact}
                aria-label={lang === 'zh' ? '可联系信息' : 'Contact details'}
                onChange={(e) => setSearch({ ...search, contact: e.target.value })}>
                <option value="all">{lang === 'zh' ? '全部记录' : 'All records'}</option>
                <option value="email">{lang === 'zh' ? '留了邮箱' : 'Has email'}</option>
                <option value="order">{lang === 'zh' ? '有订单号' : 'Has order number'}</option>
                <option value="none">{lang === 'zh' ? '没有邮箱和订单号' : 'No email or order'}</option>
              </Select>
              <Input size="sm" maxW="190px" value={search.order}
                aria-label={lang === 'zh' ? '订单号' : 'Order number'}
                placeholder={lang === 'zh' ? '订单号（可输入部分）' : 'Order number (partial)'}
                onChange={(e) => setSearch({ ...search, order: e.target.value })} />
            </>
          )}
          <Select size="sm" maxW="150px" value={outcomeFilter}
            aria-label={lang === 'zh' ? '结果' : 'Outcome'}
            onChange={(e) => setOutcomeFilter(e.target.value)}>
            <option value="all">{lang === 'zh' ? '全部结果' : 'All outcomes'}</option>
            <option value="unresolved">{lang === 'zh' ? '未解决' : 'Unresolved'}</option>
          </Select>
          <Button size="sm" type="submit" isDisabled={!selectedExtension || loading}>{lang === 'zh' ? '搜索' : 'Search'}</Button>
          {(appliedSearch.phone || appliedSearch.from || appliedSearch.to || appliedSearch.order || appliedSearch.contact !== 'all') && (
            <Button size="sm" variant="ghost" onClick={clearSearch}>{lang === 'zh' ? '清除条件' : 'Clear'}</Button>
          )}
        </HStack>
      </form>
      {loadError && <Alert status="error" mb={4}><AlertIcon />{copy.callsLoadFailed}<Button ml={3} onClick={fetchData}>{lang === 'zh' ? '重试' : 'Retry'}</Button></Alert>}
      <Card>
        <CardHeader>
          <Heading size="md">{copy.recentCalls} ({copy.extension} {selectedExtension || '—'})</Heading>
          <Text fontSize="sm" color="gray.500">{copy.monitoring}</Text>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{copy.status}</Th>
                  <Th>{copy.caller}</Th>
                  {isServiceRecords && <Th>{lang === 'zh' ? '可联系信息' : 'Contact details'}</Th>}
                  <Th>{lang === 'zh' ? '结果' : 'Outcome'}</Th>
                  {isServiceRecords && <Th>{lang === 'zh' ? '纠错' : 'Corrections'}</Th>}
                  <Th>{copy.time}</Th>
                  <Th>{copy.duration}</Th>
                  <Th>{copy.reason}</Th>
                  <Th position="sticky" right={0} bg="white" zIndex={1}>{copy.actions}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {visibleCalls.length === 0 && !loading && !loadError && (
                   <Tr>
                     <Td colSpan={isServiceRecords ? 9 : 7} textAlign="center">
                       {calls.length && outcomeFilter === 'unresolved'
                         ? (lang === 'zh' ? '已加载的记录里没有未解决的电话' : 'No unresolved calls among the loaded records')
                         : copy.noRecords}
                     </Td>
                   </Tr>
                )}
                {visibleCalls.map((call) => (
                  // The whole row opens the call; the button stays for keyboard users.
                  <Tr key={call.call_id} _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => openCall(call)}>
                    <Td>
                      <Badge 
                        colorScheme={call.status === 'completed' ? 'green' : call.status === 'failed' ? 'red' : 'blue'}
                        display="flex" 
                        alignItems="center"
                        width="fit-content"
                        gap={1}
                      >
                        {call.status === 'completed' ? <FiCheckCircle /> : call.status === 'failed' ? <FiXCircle /> : <Spinner size="xs" />}
                        {getStatusLabel(call.status)}
                      </Badge>
                    </Td>
                    <Td fontWeight="medium">{getContactName(call.caller_number)}</Td>
                    {isServiceRecords && (
                      <Td>
                        <HStack spacing={1} wrap="wrap">
                          {contactBadges(call, lang).map((badge) => (
                            <Badge key={badge.kind + badge.label} colorScheme={BADGE_COLORS[badge.kind]} variant="subtle" textTransform="none">{badge.label}</Badge>
                          ))}
                          {contactBadges(call, lang).length === 0 && <Text fontSize="sm" color="gray.500">{lang === 'zh' ? '匿名访客' : 'Anonymous visitor'}</Text>}
                        </HStack>
                      </Td>
                    )}
                    <Td minW="160px"><OutcomeBadges call={call} lang={lang} /></Td>
                    {isServiceRecords && (
                      <Td>
                        {call.corrections
                          ? <Badge colorScheme="green">{lang === 'zh' ? `已纠错 ${call.corrections}` : `${call.corrections} corrected`}</Badge>
                          : <Text fontSize="sm" color="gray.400">—</Text>}
                      </Td>
                    )}
                    <Td fontSize="sm">
                      {formatCallTime(call.start_time, displayTimezone)}
                    </Td>
                    <Td>{call.duration?.toFixed(1)}s</Td>
                    <Td maxW="200px" isTruncated title={call.end_reason}>
                      {callEndReasonLabel(call.end_reason, lang)}
                    </Td>
                    <Td position="sticky" right={0} bg="white" boxShadow="-1px 0 0 var(--chakra-colors-gray-100)">
                      <Button
                         size="sm"
                         variant="outline"
                         onClick={(event) => { event.stopPropagation(); openCall(call); }}
                         aria-label={copy.viewDetails}
                      >
                        {copy.viewDetails}
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          {hasMore && !loadError && (
            <Button mt={4} size="sm" variant="outline" isLoading={loading} onClick={() => loadCalls(selectedExtension, appliedSearch, true)}>
              {lang === 'zh' ? '加载更多' : 'Load more'}
            </Button>
          )}
        </CardBody>
      </Card>
      </>)}
      </Box>
    </Box>
  );
};

export default CallHistoryPage;
